import { z } from 'zod';
import { OfferUnitIdSchema, OFFER_UNIT_PRICING_SORT_KEYS } from './offer-unit.js';
import { createPaginatedResponseSchema } from './pagination.js';

// ─── OfferUnitPriceSchema ──────────────────────────────────────
// One price row returned from GET /offers/:offerId/prices.
// Joins offer_draft_unit_prices with offer_draft_units + media-unit metadata.
// Price fields are nullable when pricing has not been set for a unit.

export const OfferUnitPriceSchema = z.object({
  unitId: OfferUnitIdSchema,
  clientPriceNet: z.number().nullable(),
  discountPercent: z.number().nullable(),
  currency: z.string(),
  // media-unit summary metadata
  city: z.string(),
  address: z.string(),
  format: z.string().nullable(),
  supplier: z.string().nullable(),
  dimensions: z.string().nullable(),
  // Catalog-consistent unit identifier (numer GRW), shown as the primary
  // mono-font id alongside supplierNumber — see docs/GLOSSARY.md.
  grwId: z.string(),
  // Raw supplier number. Nulled server-side when the caller lacks the
  // supplier.view scope for this unit's (mediaType, ownership) context — see
  // PolicyService.canViewSupplier in apps/api.
  supplierNumber: z.string().nullable(),
  // Server-computed, catalog-consistent display identifier (same policy as
  // the availability tab's resolveUnitLabel): Large-Format units resolve to
  // MARKET_CODE + digits + MATERIAL_SUFFIX; other units resolve to grwId.
  // Always non-blank — falls back to grwId, then supplierNumber (RBAC-gated),
  // then the raw unit id when the media unit join is orphaned.
  identifier: z.string(),
  mediaType: z.string().nullable(),
  formatCode: z.string().nullable(),
  region: z.string().nullable(),
  county: z.string().nullable(),
  agglomeration: z.string().nullable(),
  widthM: z.number().nullable(),
  heightM: z.number().nullable(),
  dimensionNotes: z.string().nullable(),
  audienceEstimate: z.number().nullable(),
  notes: z.string().nullable(),
  wfContactPerson: z.string().nullable(),
  locationDescription: z.string().nullable(),
  hasPhoto: z.boolean(),
});
export type OfferUnitPrice = z.infer<typeof OfferUnitPriceSchema>;

// ─── OfferPriceListQuerySchema ─────────────────────────────────
// Cursor-based pagination query params for GET /offers/:offerId/prices.
// Mirrors OfferUnitListQuerySchema's cursor/pageSize/sort fields. Pricing has
// no filters; its closed sort keys are mapped to DB expressions before paging.

export const OFFER_PRICE_SORT_KEYS = [
  'sortOrder',
  'mediaType',
  'format',
  'supplier',
  'supplierNumber',
  'grwId',
  'region',
  'county',
  'agglomeration',
  'city',
  'address',
  'dimensions',
  'audienceEstimate',
  'notes',
  'wfContactPerson',
  'locationDescription',
  ...OFFER_UNIT_PRICING_SORT_KEYS,
] as const;
export type OfferPriceSortKey = (typeof OFFER_PRICE_SORT_KEYS)[number];

export const OfferPriceListQuerySchema = z.object({
  /** Offset-based pagination cursor. Pass meta.nextCursor to fetch the next page. */
  cursor: z
    .string()
    .regex(/^\d+$/, 'cursor must be a non-negative integer string')
    .max(20)
    .optional(),
  pageSize: z.coerce.number().min(1).max(200).default(50),
  sortBy: z.enum(OFFER_PRICE_SORT_KEYS).default('sortOrder'),
  sortDir: z.enum(['asc', 'desc']).default('asc'),
});
export type OfferPriceListQuery = z.infer<typeof OfferPriceListQuerySchema>;

/** Paginated list endpoint response — replaces the bare array for `offerPricing.list`. */
export const OfferPriceListPageResponseSchema = createPaginatedResponseSchema(OfferUnitPriceSchema);
export type OfferPriceListPageResponse = z.infer<typeof OfferPriceListPageResponseSchema>;

/**
 * Unpaginated/unfiltered all-prices response for whole-offer affordances — the
 * pricing tab's "apply discount to all" flow, which needs every unit's price
 * row (and the exact row count) regardless of the loaded page.
 */
export const OfferPriceAllResponseSchema = z.array(OfferUnitPriceSchema);
export type OfferPriceAllResponse = z.infer<typeof OfferPriceAllResponseSchema>;

// ─── BatchUpdatePricesRequestSchema ───────────────────────────
// PUT body for batch upsert of price rows.
// Max 500 rows per call (REQ-7.3 / D-4).
// Each row: unitId required; clientPriceNet optional; discountPercent 0..100 inclusive; currency defaults to 'PLN'.

// clientPriceNet/discountPercent and the nine override fields below all
// follow the SAME three-wire-state rule, matching `packages/db`'s
// `UpsertPriceRow` doc comment: a key OMITTED from a row that IS otherwise
// present in the payload leaves that column's stored value untouched (never
// resent, never cleared); an explicit `null` clears it (to "unpriced" for
// clientPriceNet, "no discount" for discountPercent, "inherit the media
// unit's value" for the nine overrides); a number sets it. `currency` is the
// one exception — it has no "clear" state (the column is NOT NULL): omitted
// leaves it untouched (or defaults to 'PLN' for a unit with no price row
// yet), a string sets it.
//
// CU-86cbgnt38 QA fix: clientPriceNet/discountPercent used to be
// `.optional()` only (no `.nullable()`), which combined with the web client
// no longer resending every field on every edit to silently wipe a unit's
// price/discount whenever a DIFFERENT field on the same row changed.
//
// The nine override fields are OFFER_PRICE_OVERRIDE_FIELD_IDS
// (offer-unit-price-fields.ts) spelled out literally so `.strict()` keeps
// static per-field typing; keep the two lists in sync by hand (a unit test
// diffs the two lists — see offer-pricing.schema.test.ts).
const PriceRowSchema = z
  .object({
    unitId: OfferUnitIdSchema,
    clientPriceNet: z.number().min(0).nullable().optional(),
    discountPercent: z.number().min(0).max(100).nullable().optional(),
    // No `.default('PLN')` — a default would make "key omitted" indistinguishable
    // from "explicitly set to PLN" once parsed, defeating the leave-untouched rule.
    currency: z.string().optional(),
    // This schema itself accepts any of the nine for any unit — Zod has no
    // access to the row's media type to reject per-row. The API's
    // OfferPricingService enforces which of the nine a unit's media type
    // actually accepts (`getOfferPriceOverrideFieldIds`), rejecting a
    // disallowed field with a 422 rather than silently dropping or
    // persisting it (CU-86cbgnt38 QA fix, `sec-frontend-only-permission`
    // review finding — a caller bypassing the UI could otherwise persist a
    // field outside a unit's offered set and corrupt that offer's totals).
    catalogPrice: z.number().min(0).nullable().optional(),
    discountedPrice: z.number().min(0).nullable().optional(),
    lastMinutePrice: z.number().min(0).nullable().optional(),
    starterPrice: z.number().min(0).nullable().optional(),
    clientProduction: z.number().min(0).nullable().optional(),
    clientInstallation: z.number().min(0).nullable().optional(),
    clientDismantling: z.number().min(0).nullable().optional(),
    clientAdditionalFees: z.number().min(0).nullable().optional(),
    muralEcoProduction: z.number().min(0).nullable().optional(),
  })
  .strict();
export type PriceRow = z.infer<typeof PriceRowSchema>;

export const BatchUpdatePricesRequestSchema = z
  .object({
    rows: z.array(PriceRowSchema).max(500),
    updatedAt: z.coerce.date(),
  })
  .strict();
export type BatchUpdatePricesRequest = z.infer<typeof BatchUpdatePricesRequestSchema>;

// ─── OfferPricingTotalsSchema ─────────────────────────────────
// Response from GET /offers/:offerId/prices/totals.
// All monetary values are server-computed using numeric SQL arithmetic.
// grandTotalNet equals subtotalAfterDiscount (KIK is an internal broker fee, not a client charge).
// subtotalNet now sums clientPriceNet plus the four OFFER_PRICE_SUM_FIELD_IDS
// core line items (clientProduction/muralEcoProduction/clientInstallation/
// clientDismantling) over priced units, mirroring computePriceBreakdown's
// core-line-items sum (price-breakdown.ts). additionalServicesNet sums
// clientAdditionalFees separately — it stays OUTSIDE subtotalNet, mirroring
// that module's `isOptional` line item.

export const OfferPricingTotalsSchema = z.object({
  subtotalNet: z.number(),
  totalDiscountAmount: z.number(),
  subtotalAfterDiscount: z.number(),
  kikAmount: z.number(),
  grandTotalNet: z.number(),
  /** Sum of clientAdditionalFees (override or media-unit fallback) over priced units — reported outside subtotalNet. */
  additionalServicesNet: z.number(),
  currency: z.string(),
});
export type OfferPricingTotals = z.infer<typeof OfferPricingTotalsSchema>;
